import { oklchToCss, parseColor } from "@/document/model/color";
import {
  createLinearGradientPaint,
  createRadialGradientPaint,
  createSolidPaint,
  normalizeGradientPaint,
  type GradientPaint,
  type GradientStop,
  type Paint,
} from "@/document/model/styles";
import { createId } from "@/lib/ids";
import {
  defaultColorParamValue,
  defaultGradientParamValue,
  type ScriptGradientParamValue,
} from "@/scripting/scriptParams";

export function colorParamToPaint(css: string): Paint {
  return createSolidPaint(css.trim() || defaultColorParamValue());
}

export function paintToColorParam(paint: Paint): string {
  if (paint.type === "solid") {
    return oklchToCss(parseColor(paint.color));
  }
  if (paint.type === "linearGradient" || paint.type === "radialGradient") {
    const first = normalizeGradientPaint(paint).stops[0]?.color;
    return first ? oklchToCss(parseColor(first)) : defaultColorParamValue();
  }
  return defaultColorParamValue();
}

export function gradientParamToPaint(value: ScriptGradientParamValue): GradientPaint {
  const stops = value.stops.length >= 2
    ? value.stops
    : defaultGradientParamValue().stops;
  const base = value.type === "radial"
    ? createRadialGradientPaint(stops[0]!.color, stops[stops.length - 1]!.color)
    : createLinearGradientPaint(stops[0]!.color, stops[stops.length - 1]!.color);

  const nextStops: GradientStop[] = stops.map(stop => ({
    id: createId("stop"),
    position: stop.position,
    color: parseColor(stop.color),
  }));

  if (base.type === "linearGradient") {
    return normalizeGradientPaint({
      ...base,
      angle: value.angle ?? 90,
      colorSpace: value.colorSpace === "rgb" ? "rgb" : "oklch",
      stops: nextStops,
      start: value.start,
      end: value.end,
    });
  }

  return normalizeGradientPaint({
    ...base,
    radius: value.radius ?? 0.5,
    colorSpace: value.colorSpace === "rgb" ? "rgb" : "oklch",
    stops: nextStops,
    center: value.center ?? { x: 0.5, y: 0.5 },
  });
}

export function paintToGradientParam(paint: Paint): ScriptGradientParamValue {
  if (paint.type !== "linearGradient" && paint.type !== "radialGradient") {
    const color = paintToColorParam(paint);
    return {
      ...defaultGradientParamValue(),
      stops: [
        { position: 0, color },
        { position: 1, color: "#ffffff" },
      ],
    };
  }
  const normalized = normalizeGradientPaint(paint);
  return {
    type: normalized.type === "radialGradient" ? "radial" : "linear",
    angle: normalized.type === "linearGradient" ? normalized.angle : undefined,
    radius: normalized.type === "radialGradient" ? normalized.radius : undefined,
    colorSpace: normalized.colorSpace === "rgb" ? "rgb" : "oklch",
    stops: normalized.stops.map(stop => ({
      position: stop.position,
      color: oklchToCss(parseColor(stop.color)),
    })),
    start: normalized.type === "linearGradient" ? normalized.start : undefined,
    end: normalized.type === "linearGradient" ? normalized.end : undefined,
    center: normalized.type === "radialGradient" ? normalized.center : undefined,
  };
}
